import React, { useState, useEffect } from 'react';

import { useOnboardingStore } from '../../../store/onboarding.store';

interface BillingDateStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function BillingDateStep({ onNext, onBack }: BillingDateStepProps) {
  const { billingData, setBillingData } = useOnboardingStore();

  const [selectedStrategy, setSelectedStrategy] = useState<
    'monthly_fixed_date' | 'on_signup' | null
  >(billingData.billingStrategy || 'monthly_fixed_date');
  const [selectedDay, setSelectedDay] = useState<number | null>(
    billingData.fixedBillingDay || 1,
  );
  const [customDay, setCustomDay] = useState<string>('');

  useEffect(() => {
    if (selectedStrategy) {
      setBillingData({
        billingStrategy: selectedStrategy,
        fixedBillingDay: selectedStrategy === 'monthly_fixed_date' && selectedDay !== null ? selectedDay : null,
      });
    }
  }, [selectedStrategy, selectedDay, setBillingData]);

  const handleDaySelect = (day: number) => {
    setCustomDay('');
    setSelectedDay(day);
  };

  const handleCustomDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    if (numericValue === '') {
      setCustomDay('');
      return;
    }

    const day = parseInt(numericValue, 10);
    if (day >= 1 && day <= 28) {
      setCustomDay(numericValue);
      setSelectedDay(day);
    } else if (day > 28) {
      setCustomDay('28');
      setSelectedDay(28);
    }
  };

  const canContinue =
    selectedStrategy === 'on_signup' ||
    (selectedStrategy === 'monthly_fixed_date' && selectedDay !== null);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Choose Billing Date Strategy</h2>
        <p className="text-[#D1D5DB] mt-2">When would you like to bill your members?</p>
      </div>

      <div className="space-y-4">
        {/* Fixed Monthly Date Option */}
        <button
          onClick={() => setSelectedStrategy('monthly_fixed_date')}
          className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
            selectedStrategy === 'monthly_fixed_date'
              ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
              : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
          }`}
        >
          <h3 className="text-lg font-bold text-[#EBFFEE] mb-2">Fixed Monthly Date</h3>
          <p className="text-[#D1D5DB] text-sm mb-4">
            Bill all members on the same day each month (e.g., 1st, 15th)
          </p>

          {selectedStrategy === 'monthly_fixed_date' && (
            <div className="mt-4 pt-4 border-t border-[#504A4A]">
              <p className="text-sm text-[#D1D5DB] mb-3">Select billing day:</p>
              <div className="flex gap-2 flex-wrap mb-4">
                {[1, 5, 10, 15, 20, 25].map(day => (
                  <button
                    key={day}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDaySelect(day);
                    }}
                    className={`w-14 h-14 rounded-lg border-2 transition-all ${
                      selectedDay === day
                        ? 'bg-[#9B1421] border-[#9B1421] text-white'
                        : 'bg-[#363232] border-[#504A4A] text-[#D1D5DB] hover:border-[#9B1421]/50'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              <div className="mt-2">
                <p className="text-sm text-[#D1D5DB] mb-2">Or enter custom day (1-28):</p>
                <input
                  type="text"
                  value={customDay}
                  onChange={handleCustomDayChange}
                  placeholder="e.g., 3"
                  className="w-20 px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-lg text-white text-center focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                  maxLength={2}
                />
              </div>
              {selectedDay !== null && (
                <p className="text-sm text-rollops-accent mt-3 font-semibold">
                  Members will be billed on the {selectedDay}
                  {selectedDay === 1
                    ? 'st'
                    : selectedDay === 2
                      ? 'nd'
                      : selectedDay === 3
                        ? 'rd'
                        : 'th'}{' '}
                  of each month
                </p>
              )}
            </div>
          )}
        </button>

        {/* On Signup Option */}
        <button
          onClick={() => setSelectedStrategy('on_signup')}
          className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
            selectedStrategy === 'on_signup'
              ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
              : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
          }`}
        >
          <h3 className="text-lg font-bold text-[#EBFFEE] mb-2">Bill on Signup</h3>
          <p className="text-[#D1D5DB] text-sm">
            Bill each member on their signup anniversary date each month
          </p>
        </button>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-[#363232] text-[#EBFFEE] rounded-lg hover:bg-[#504A4A] transition-colors"
        >
          Go Back
        </button>
        <button
          onClick={onNext}
          disabled={!canContinue}
          className={`px-6 py-3 rounded-lg transition-colors ${
            canContinue
              ? 'bg-[#9B1421] text-white hover:bg-[#C01F2D]'
              : 'bg-[#363232] text-[#D1D5DB] cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}







